import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Plus, Users, Trash, PencilSimple, Printer, CurrencyDollar,
  Check, X, Bed, Truck, Receipt, Copy, ArrowsClockwise,
} from '@phosphor-icons/react';
import api from '../../utils/api';

/**
 * StaffInvoicesSection — producer panel inside Manage Entries → Staff Charges
 * tab. Tracks staff who get stalls/RV/tie-out + line-item extras, with
 * payment status, PDF export, and a Square checkout link.
 *
 * Each invoice mirrors its stall/RV picks into `spot_reservations` so the
 * stall map automatically locks those spots out from riders.
 */
export const StaffInvoicesSection = ({ showId, showName }) => {
  const [invoices, setInvoices] = useState([]);
  const [summary, setSummary] = useState({ total_invoices: 0, outstanding: 0, collected: 0 });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | invoice doc
  const [actionBusy, setActionBusy] = useState(null);
  const [staffHolds, setStaffHolds] = useState([]); // free producer-held staff reservations (no invoice)
  const [showPricing, setShowPricing] = useState({ stall_price: 0, rv_hookup_price: 0, stall_price_per_day: 0, rv_hookup_price_per_day: 0 });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [invRes, resRes, showRes] = await Promise.all([
        api.get(`/shows/${showId}/staff-invoices`),
        api.get(`/shows/${showId}/spot-reservations`).catch(() => ({ data: [] })),
        api.get(`/shows/${showId}`).catch(() => ({ data: {} })),
      ]);
      setInvoices(invRes.data.invoices || []);
      setSummary(invRes.data.summary || { total_invoices: 0, outstanding: 0, collected: 0 });
      // Pull standard stall/RV pricing so we can show producers the
      // "equivalent value" of each free staff hold — easier to figure
      // out what to bill staff at the end of the show.
      setShowPricing({
        stall_price: Number(showRes.data?.stall_price) || 0,
        rv_hookup_price: Number(showRes.data?.rv_hookup_price) || 0,
        stall_price_per_day: Number(showRes.data?.stall_price_per_day) || 0,
        rv_hookup_price_per_day: Number(showRes.data?.rv_hookup_price_per_day) || 0,
      });
      // Filter to reservations the producer has marked as staff holds that
      // are NOT already tracked under a staff_invoice (those show in the
      // table below). Group by staff name for a clean rollup.
      const holds = (resRes.data || []).filter(
        r => r.reserved_by_producer && !r.staff_invoice_id
      );
      setStaffHolds(holds);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load staff charges');
    } finally {
      setLoading(false);
    }
  }, [showId]);

  useEffect(() => { load(); }, [load]);

  const handleSaved = async () => { setEditing(null); await load(); };

  const handleDelete = async (inv) => {
    if (!window.confirm(`Delete staff charge for ${inv.staff_name}? This also releases any stall/RV spots locked for them.`)) return;
    setActionBusy(inv.id);
    try {
      await api.delete(`/shows/${showId}/staff-invoices/${inv.id}`);
      toast.success('Staff charge removed');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to delete');
    } finally { setActionBusy(null); }
  };

  const handleMarkPaid = async (inv) => {
    const method = window.prompt('How was this paid? (e.g., Cash, Venmo, Deducted from hours)', 'Cash');
    if (method === null) return;
    setActionBusy(inv.id);
    try {
      await api.post(`/shows/${showId}/staff-invoices/${inv.id}/mark-paid`, { method });
      toast.success(`Marked paid (${method})`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to mark paid');
    } finally { setActionBusy(null); }
  };

  const handleMarkUnpaid = async (inv) => {
    if (!window.confirm(`Flip ${inv.staff_name}'s invoice back to UNPAID?`)) return;
    setActionBusy(inv.id);
    try {
      await api.post(`/shows/${showId}/staff-invoices/${inv.id}/mark-unpaid`);
      toast.success('Marked unpaid');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to mark unpaid');
    } finally { setActionBusy(null); }
  };

  const handleCheckoutLink = async (inv) => {
    setActionBusy(inv.id);
    try {
      const { data } = await api.post(`/shows/${showId}/staff-invoices/${inv.id}/checkout-link`, {
        origin_url: window.location.origin,
      });
      if (data?.url) {
        try {
          await navigator.clipboard.writeText(data.url);
          toast.success('Square checkout link copied to clipboard');
        } catch {
          toast.success('Link generated');
        }
        window.open(data.url, '_blank', 'noopener');
        await load();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to generate checkout link');
    } finally { setActionBusy(null); }
  };

  const handleDownloadPdf = async (inv) => {
    setActionBusy(inv.id);
    try {
      const res = await api.get(`/shows/${showId}/staff-invoices/${inv.id}/invoice.pdf`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice_${(inv.staff_name || 'staff').replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to download PDF');
    } finally { setActionBusy(null); }
  };

  // Group free staff holds by staff_name → list of spots.
  const staffHoldsByName = useMemo(() => {
    // Look up the standard "weekend" pricing for each type — that's the
    // dollar value we show producers so they can bill staff for the
    // equivalent of what a regular rider would have paid for the spot.
    const stallStd = showPricing.stall_price || (showPricing.stall_price_per_day ? showPricing.stall_price_per_day * 3 : 0);
    const rvStd = showPricing.rv_hookup_price || (showPricing.rv_hookup_price_per_day ? showPricing.rv_hookup_price_per_day * 3 : 0);
    const equivFor = (r) => {
      // Spot's own stored fee wins (set when invoiced); otherwise use the
      // show's standard pricing for that type as an estimate.
      const stored = Number(r.fee || 0);
      if (stored > 0) return stored;
      return r.type === 'stall' ? stallStd : rvStd;
    };
    const map = new Map();
    for (const r of staffHolds) {
      const name = (r.rider_name || '').trim() || 'Unnamed staff';
      if (!map.has(name)) map.set(name, { stalls: [], rvs: [], notes: '', equivTotal: 0 });
      const bucket = map.get(name);
      const eq = equivFor(r);
      const enriched = { ...r, _equivFee: eq };
      if (r.type === 'stall') bucket.stalls.push(enriched);
      else bucket.rvs.push(enriched);
      bucket.equivTotal += eq;
      if (r.staff_notes && !bucket.notes) bucket.notes = r.staff_notes;
    }
    return Array.from(map.entries())
      .map(([name, v]) => ({ name, ...v }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [staffHolds, showPricing]);

  const handleReleaseHold = async (reservationId, label) => {
    if (!window.confirm(`Release this staff hold for ${label}? The spot becomes available to riders again.`)) return;
    setActionBusy(reservationId);
    try {
      await api.delete(`/spot-reservations/${reservationId}`);
      toast.success('Staff hold released');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to release');
    } finally { setActionBusy(null); }
  };

  const handleRenameHold = async (reservation) => {
    const label = `${reservation.barn || 'Main'} ${reservation.row || ''}-${reservation.spot_number}`;
    const current = reservation.rider_name || '';
    const next = window.prompt(`Rename staff hold for ${label}\n\nThis transfers the spot to a specific staff member without losing it.`, current);
    if (next === null) return; // cancelled
    const trimmed = next.trim();
    if (!trimmed) {
      toast.error('Name cannot be empty');
      return;
    }
    if (trimmed === current) return;
    setActionBusy(reservation.id);
    try {
      await api.patch(`/spot-reservations/${reservation.id}`, { rider_name: trimmed });
      toast.success(`Staff hold reassigned to ${trimmed}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to rename');
    } finally { setActionBusy(null); }
  };

  return (
    <div data-testid="staff-invoices-section">
      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
        <StatCard label="Invoices" value={summary.total_invoices} color="#9E9E9E" testid="staff-stat-count" />
        <StatCard label="Outstanding" value={`$${(summary.outstanding || 0).toFixed(2)}`} color="#FF9500" testid="staff-stat-outstanding" />
        <StatCard label="Collected" value={`$${(summary.collected || 0).toFixed(2)}`} color="#34C759" testid="staff-stat-collected" />
      </div>

      {/* Free Staff Reservations (producer-held, no invoice) */}
      {staffHoldsByName.length > 0 && (
        <div className="bg-[#0A0A0A] border border-[#1F1F1F] rounded-lg p-4 mb-5" data-testid="staff-holds-panel">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Bed size={14} className="text-[#34C759]" weight="duotone" />
              <span className="text-[11px] uppercase tracking-wider text-[#9E9E9E] font-semibold">
                Staff Reservations · Free Holds ({staffHolds.length})
              </span>
            </div>
            <span className="text-[9px] text-[#9E9E9E] uppercase">Mirrors the "Reserve for Staff" panel on Show Detail</span>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
            {staffHoldsByName.map((p) => (
              <div
                key={p.name}
                className="border border-[#262626] rounded p-3 bg-[#0F0F0F]"
                data-testid={`staff-hold-${p.name.replace(/\s+/g, '-')}`}
              >
                <div className="flex items-baseline justify-between mb-2 gap-2">
                  <div className="text-sm font-bold text-white truncate">{p.name}</div>
                  <div className="flex items-baseline gap-2 flex-shrink-0">
                    <div className="text-[10px] text-[#9E9E9E]">
                      {p.stalls.length} stall{p.stalls.length === 1 ? '' : 's'} · {p.rvs.length} RV
                    </div>
                    <div
                      className="text-sm font-black text-[#FF9500]"
                      style={{ fontFamily: 'Outfit' }}
                      title="Equivalent dollar value at standard rider pricing"
                      data-testid={`staff-hold-total-${p.name.replace(/\s+/g, '-')}`}
                    >
                      ${p.equivTotal.toFixed(0)}
                    </div>
                  </div>
                </div>
                {p.notes && (
                  <div className="text-[10px] text-[#9E9E9E] italic mb-2">"{p.notes}"</div>
                )}
                <div className="flex flex-col gap-1">
                  {p.stalls.map((s) => (
                    <SpotChip key={s.id} icon={Bed} color="#34C759" reservation={s} onRelease={handleReleaseHold} onRename={handleRenameHold} busy={actionBusy === s.id} />
                  ))}
                  {p.rvs.map((s) => (
                    <SpotChip key={s.id} icon={Truck} color="#0096FF" reservation={s} onRelease={handleReleaseHold} onRename={handleRenameHold} busy={actionBusy === s.id} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
        <div className="text-xs text-[#9E9E9E]">
          Track stalls, RV, tie-out + extras given to staff. Stalls picked here are auto-locked on the rider stall map.
        </div>
        <div className="flex gap-2">
          <button
            onClick={load}
            className="px-2.5 py-2 bg-[#0A0A0A] border border-[#262626] hover:border-[#FF9500] text-[#9E9E9E] hover:text-white rounded transition-colors"
            title="Refresh"
            data-testid="staff-invoices-refresh"
          >
            <ArrowsClockwise size={14} />
          </button>
          <button
            onClick={() => setEditing('new')}
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#FF9500] hover:bg-[#FFA500] text-black text-xs font-bold rounded transition-colors"
            data-testid="staff-invoices-add-btn"
          >
            <Plus size={14} weight="bold" /> Add Staff Charge
          </button>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="py-8 text-center text-[#9E9E9E] text-sm">Loading…</div>
      ) : invoices.length === 0 ? (
        <div className="py-12 text-center bg-[#0A0A0A] border border-dashed border-[#262626] rounded-lg" data-testid="staff-invoices-empty">
          <Users size={32} className="text-[#262626] mx-auto mb-2" />
          <p className="text-sm text-[#9E9E9E]">No staff charges yet.</p>
          <p className="text-xs text-[#555] mt-1">Click "Add Staff Charge" to create one.</p>
        </div>
      ) : (
        <div className="bg-[#0A0A0A] border border-[#1F1F1F] rounded overflow-x-auto">
          <table className="w-full text-xs" data-testid="staff-invoices-table">
            <thead className="bg-[#161616] text-left">
              <tr className="text-[10px] uppercase text-[#9E9E9E]">
                <th className="px-3 py-2 font-semibold">Staff</th>
                <th className="px-3 py-2 font-semibold text-center">Stalls</th>
                <th className="px-3 py-2 font-semibold text-center">RV</th>
                <th className="px-3 py-2 font-semibold text-center">Tie-Out</th>
                <th className="px-3 py-2 font-semibold text-center">Extras</th>
                <th className="px-3 py-2 font-semibold text-right">Total</th>
                <th className="px-3 py-2 font-semibold">Status</th>
                <th className="px-3 py-2 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => (
                <tr key={inv.id} className="border-t border-[#1F1F1F]" data-testid={`staff-invoice-row-${inv.id}`}>
                  <td className="px-3 py-2 text-white font-semibold whitespace-nowrap">{inv.staff_name}</td>
                  <td className="px-3 py-2 text-center text-white">{inv.stall_count}</td>
                  <td className="px-3 py-2 text-center text-white">{inv.rv_count}</td>
                  <td className="px-3 py-2 text-center text-white">{inv.tie_out_count}</td>
                  <td className="px-3 py-2 text-center text-white">{(inv.extras || []).length}</td>
                  <td className="px-3 py-2 text-right text-white font-bold">${(inv.grand_total || 0).toFixed(2)}</td>
                  <td className="px-3 py-2">
                    {inv.status === 'unpaid' && (
                      <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-[#FF9500]/15 text-[#FF9500]">UNPAID</span>
                    )}
                    {inv.status === 'paid_offline' && (
                      <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-[#34C759]/15 text-[#34C759]" title={inv.payment_method || ''}>
                        PAID · {inv.payment_method || 'offline'}
                      </span>
                    )}
                    {inv.status === 'paid_online' && (
                      <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-[#34C759]/15 text-[#34C759]">PAID · ONLINE</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex items-center gap-1 justify-end flex-wrap">
                      <ActionBtn icon={PencilSimple} title="Edit" testid={`staff-invoice-edit-${inv.id}`} onClick={() => setEditing(inv)} />
                      <ActionBtn icon={Printer} title="Download PDF invoice" testid={`staff-invoice-pdf-${inv.id}`} onClick={() => handleDownloadPdf(inv)} disabled={actionBusy === inv.id} color="#0096FF" />
                      <ActionBtn icon={CurrencyDollar} title="Send Square checkout link" testid={`staff-invoice-square-${inv.id}`} onClick={() => handleCheckoutLink(inv)} disabled={actionBusy === inv.id} color="#0096FF" />
                      {inv.status === 'unpaid' ? (
                        <ActionBtn icon={Check} title="Mark paid" testid={`staff-invoice-mark-paid-${inv.id}`} onClick={() => handleMarkPaid(inv)} disabled={actionBusy === inv.id} color="#34C759" />
                      ) : (
                        <ActionBtn icon={X} title="Mark unpaid" testid={`staff-invoice-mark-unpaid-${inv.id}`} onClick={() => handleMarkUnpaid(inv)} disabled={actionBusy === inv.id} color="#FF9500" />
                      )}
                      <ActionBtn icon={Trash} title="Delete" testid={`staff-invoice-delete-${inv.id}`} onClick={() => handleDelete(inv)} disabled={actionBusy === inv.id} color="#FF3B30" />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <StaffInvoiceModal
          showId={showId}
          showName={showName}
          invoice={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
};

const StatCard = ({ label, value, color, testid }) => (
  <div className="bg-[#0A0A0A] border border-[#1F1F1F] rounded p-3" data-testid={testid}>
    <p className="text-[9px] uppercase tracking-wider text-[#9E9E9E]">{label}</p>
    <p className="text-2xl font-black text-white mt-1" style={{ fontFamily: 'Outfit', color }}>{value}</p>
  </div>
);

const ActionBtn = ({ icon: Icon, title, testid, onClick, disabled, color = '#9E9E9E' }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    title={title}
    data-testid={testid}
    className="inline-flex items-center justify-center w-7 h-7 rounded border border-[#262626] hover:border-current disabled:opacity-40 transition-colors"
    style={{ color }}
  >
    <Icon size={12} weight="bold" />
  </button>
);

const SpotChip = ({ icon: Icon, color, reservation, onRelease, onRename, busy }) => {
  const label = `${reservation.barn || 'Main'} ${reservation.row || ''}-${reservation.spot_number}`;
  const eq = Number(reservation._equivFee || 0);
  return (
    <div className="flex items-center gap-2 text-[11px]" data-testid={`staff-hold-spot-${reservation.id}`}>
      <Icon size={12} style={{ color }} weight="duotone" />
      <span className="text-white font-mono">{label}</span>
      <span className="text-[#555] text-[9px] uppercase">{reservation.type}</span>
      {eq > 0 && (
        <span className="text-[#FF9500] font-mono text-[10px]" title="Equivalent value at standard rider pricing">
          ${eq.toFixed(0)}
        </span>
      )}
      <div className="ml-auto inline-flex items-center gap-2">
        {onRename && (
          <button
            onClick={() => onRename(reservation)}
            disabled={busy}
            title="Edit / rename staff hold (e.g. transfer to a specific staff member)"
            data-testid={`staff-hold-rename-${reservation.id}`}
            className="text-[#0096FF] hover:opacity-75 disabled:opacity-30 transition-opacity"
          >
            <PencilSimple size={11} weight="bold" />
          </button>
        )}
        <button
          onClick={() => onRelease(reservation.id, label)}
          disabled={busy}
          title="Release staff hold"
          data-testid={`staff-hold-release-${reservation.id}`}
          className="text-[#FF3B30] hover:opacity-75 disabled:opacity-30 transition-opacity"
        >
          <Trash size={11} weight="bold" />
        </button>
      </div>
    </div>
  );
};


// ===============================================
// Modal for create/edit
// ===============================================
const StaffInvoiceModal = ({ showId, showName, invoice, onClose, onSaved }) => {
  const isEdit = !!invoice;
  const [staffName, setStaffName] = useState(invoice?.staff_name || '');
  const [stallUnit, setStallUnit] = useState(invoice?.stall_unit_price ?? 0);
  const [rvUnit, setRvUnit] = useState(invoice?.rv_unit_price ?? 0);
  const [tieOutUnit, setTieOutUnit] = useState(invoice?.tie_out_unit_price ?? 0);
  const [tieOutCount, setTieOutCount] = useState(invoice?.tie_out_count ?? 0);
  const [stalls, setStalls] = useState(invoice?.stall_reservations || []);
  const [rvs, setRvs] = useState(invoice?.rv_reservations || []);
  const [extras, setExtras] = useState(invoice?.extras || []);
  const [notes, setNotes] = useState(invoice?.notes || '');
  const [saving, setSaving] = useState(false);
  const [spotMap, setSpotMap] = useState(null);
  const [showPicker, setShowPicker] = useState(null); // 'stall' | 'rv' | null

  // Load show defaults + current spot map on open.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/shows/${showId}/spot-map`);
        if (cancelled) return;
        setSpotMap(data);
        // If this is a new invoice and prices haven't been set, hydrate from show defaults.
        if (!invoice) {
          setStallUnit((v) => v || Number(data.stall_price || 0));
          setRvUnit((v) => v || Number(data.rv_price || 0));
        }
        // Tie-out price defaults from show settings
        const { data: showDoc } = await api.get(`/shows/${showId}`);
        if (cancelled) return;
        if (!invoice) {
          setTieOutUnit((v) => v || Number(showDoc?.tie_out_fee_price || 0));
        }
      } catch (e) {
        // Non-fatal — picker just won't render available spots.
        console.warn('Failed to load spot map', e);
      }
    })();
    return () => { cancelled = true; };
  }, [showId, invoice]);

  const totals = useMemo(() => {
    const stallsTotal = (Number(stallUnit) || 0) * stalls.length;
    const rvTotal = (Number(rvUnit) || 0) * rvs.length;
    const tieOutTotal = (Number(tieOutUnit) || 0) * (Number(tieOutCount) || 0);
    const extrasTotal = extras.reduce((s, e) => s + (Number(e.amount) || 0), 0);
    return {
      stallsTotal, rvTotal, tieOutTotal, extrasTotal,
      grand: stallsTotal + rvTotal + tieOutTotal + extrasTotal,
    };
  }, [stalls, rvs, stallUnit, rvUnit, tieOutCount, tieOutUnit, extras]);

  const handleSave = async () => {
    if (!staffName.trim()) {
      toast.error('Staff name is required');
      return;
    }
    setSaving(true);
    try {
      const body = {
        staff_name: staffName.trim(),
        stall_unit_price: Number(stallUnit) || 0,
        rv_unit_price: Number(rvUnit) || 0,
        tie_out_unit_price: Number(tieOutUnit) || 0,
        stall_reservations: stalls,
        rv_reservations: rvs,
        tie_out_count: Number(tieOutCount) || 0,
        extras: extras.filter((e) => (e.label || '').trim()),
        notes,
      };
      if (isEdit) {
        await api.patch(`/shows/${showId}/staff-invoices/${invoice.id}`, body);
        toast.success(`Updated charges for ${staffName}`);
      } else {
        await api.post(`/shows/${showId}/staff-invoices`, body);
        toast.success(`Saved charges for ${staffName}`);
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const removeSpot = (type, idx) => {
    if (type === 'stall') setStalls((prev) => prev.filter((_, i) => i !== idx));
    else setRvs((prev) => prev.filter((_, i) => i !== idx));
  };

  const addExtra = () => setExtras((prev) => [...prev, { label: '', amount: 0 }]);
  const updateExtra = (i, field, val) => {
    setExtras((prev) => prev.map((e, idx) => idx === i ? { ...e, [field]: val } : e));
  };
  const removeExtra = (i) => setExtras((prev) => prev.filter((_, idx) => idx !== i));

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur flex items-center justify-center p-3 sm:p-6 overflow-y-auto" onClick={onClose}>
      <div
        className="bg-[#0A0A0A] border border-[#1F1F1F] rounded-xl w-full max-w-3xl my-6"
        onClick={(e) => e.stopPropagation()}
        data-testid="staff-invoice-modal"
      >
        <div className="px-5 py-4 border-b border-[#1F1F1F] flex items-center justify-between sticky top-0 bg-[#0A0A0A] z-10">
          <div className="flex items-center gap-2">
            <Receipt size={18} className="text-[#FF9500]" />
            <h2 className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit' }}>
              {isEdit ? `Edit Staff Charge` : 'New Staff Charge'}
            </h2>
          </div>
          <button onClick={onClose} className="text-[#9E9E9E] hover:text-white" data-testid="staff-invoice-modal-close">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-5 max-h-[calc(90vh-130px)] overflow-y-auto">
          {/* Staff name */}
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-[#9E9E9E] mb-1">Staff name</label>
            <input
              type="text"
              value={staffName}
              onChange={(e) => setStaffName(e.target.value)}
              placeholder="e.g., Jane Doe (Barn Manager)"
              className="w-full px-3 py-2 bg-[#161616] border border-[#262626] rounded text-white text-sm focus:outline-none focus:border-[#FF9500]"
              data-testid="staff-invoice-name-input"
              autoFocus
            />
          </div>

          {/* Unit prices */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <PriceInput label="Stall unit price" value={stallUnit} onChange={setStallUnit} testid="staff-invoice-stall-price" />
            <PriceInput label="RV unit price" value={rvUnit} onChange={setRvUnit} testid="staff-invoice-rv-price" />
            <PriceInput label="Tie-out unit price" value={tieOutUnit} onChange={setTieOutUnit} testid="staff-invoice-tieout-price" />
          </div>

          {/* Stalls picker */}
          <SpotSection
            label="Stalls"
            icon={Bed}
            color="#34C759"
            unit={stallUnit}
            spots={stalls}
            onAdd={() => setShowPicker('stall')}
            onRemove={(i) => removeSpot('stall', i)}
            testid="stalls"
          />

          {/* RV picker */}
          <SpotSection
            label="RV Hookups"
            icon={Truck}
            color="#5E5CE6"
            unit={rvUnit}
            spots={rvs}
            onAdd={() => setShowPicker('rv')}
            onRemove={(i) => removeSpot('rv', i)}
            testid="rvs"
          />

          {/* Tie-out */}
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-[#9E9E9E] mb-1">Tie-Out count</label>
            <input
              type="number" min="0" value={tieOutCount}
              onChange={(e) => setTieOutCount(Math.max(0, parseInt(e.target.value || '0', 10)))}
              className="w-32 px-3 py-2 bg-[#161616] border border-[#262626] rounded text-white text-sm focus:outline-none focus:border-[#FF9500]"
              data-testid="staff-invoice-tieout-count"
            />
          </div>

          {/* Extras */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-[10px] uppercase tracking-wider text-[#9E9E9E]">Extras (electric, shavings, etc.)</label>
              <button onClick={addExtra} className="text-[#FF9500] hover:text-[#FFA500] text-xs font-bold inline-flex items-center gap-1" data-testid="staff-invoice-add-extra">
                <Plus size={12} weight="bold" /> Add line
              </button>
            </div>
            {extras.length === 0 ? (
              <p className="text-xs text-[#555] italic">No extras.</p>
            ) : (
              <div className="space-y-2">
                {extras.map((e, i) => (
                  <div key={i} className="flex gap-2" data-testid={`staff-invoice-extra-${i}`}>
                    <input
                      type="text" placeholder="Label (e.g., Shavings)" value={e.label}
                      onChange={(ev) => updateExtra(i, 'label', ev.target.value)}
                      className="flex-1 px-3 py-1.5 bg-[#161616] border border-[#262626] rounded text-white text-sm focus:outline-none focus:border-[#FF9500]"
                    />
                    <input
                      type="number" placeholder="$0.00" step="0.01" value={e.amount}
                      onChange={(ev) => updateExtra(i, 'amount', parseFloat(ev.target.value) || 0)}
                      className="w-28 px-3 py-1.5 bg-[#161616] border border-[#262626] rounded text-white text-sm focus:outline-none focus:border-[#FF9500]"
                    />
                    <button onClick={() => removeExtra(i)} className="px-2 text-[#FF3B30] hover:bg-[#FF3B30]/10 rounded">
                      <Trash size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Notes */}
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-[#9E9E9E] mb-1">Notes (optional)</label>
            <textarea
              value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
              placeholder="e.g., Comp for setup help, 3 nights worked"
              className="w-full px-3 py-2 bg-[#161616] border border-[#262626] rounded text-white text-sm focus:outline-none focus:border-[#FF9500] resize-none"
              data-testid="staff-invoice-notes"
            />
          </div>

          {/* Totals preview */}
          <div className="bg-[#0F0F0F] border border-[#1F1F1F] rounded-lg p-4" data-testid="staff-invoice-totals">
            <div className="text-[10px] uppercase tracking-wider text-[#9E9E9E] mb-2">Totals preview</div>
            <TotalRow label={`Stalls (${stalls.length} × $${Number(stallUnit).toFixed(2)})`} value={totals.stallsTotal} />
            <TotalRow label={`RV (${rvs.length} × $${Number(rvUnit).toFixed(2)})`} value={totals.rvTotal} />
            <TotalRow label={`Tie-Out (${tieOutCount} × $${Number(tieOutUnit).toFixed(2)})`} value={totals.tieOutTotal} />
            <TotalRow label={`Extras (${extras.length})`} value={totals.extrasTotal} />
            <div className="border-t border-[#1F1F1F] mt-2 pt-2 flex items-center justify-between">
              <span className="text-sm font-bold text-white">Grand Total</span>
              <span className="text-2xl font-black text-[#FF9500]" style={{ fontFamily: 'Outfit' }}>
                ${totals.grand.toFixed(2)}
              </span>
            </div>
          </div>
        </div>

        <div className="px-5 py-4 border-t border-[#1F1F1F] flex items-center justify-end gap-2 bg-[#0A0A0A]">
          <button onClick={onClose} className="px-4 py-2 text-[#9E9E9E] hover:text-white text-sm font-bold">Cancel</button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-5 py-2 bg-[#FF9500] hover:bg-[#FFA500] disabled:opacity-60 text-black text-sm font-bold rounded inline-flex items-center gap-2"
            data-testid="staff-invoice-save-btn"
          >
            <Check size={14} weight="bold" /> {saving ? 'Saving…' : (isEdit ? 'Update Charge' : 'Save Charge')}
          </button>
        </div>
      </div>

      {showPicker && (
        <SpotPickerModal
          type={showPicker}
          spotMap={spotMap}
          selected={showPicker === 'stall' ? stalls : rvs}
          alreadySelectedOther={showPicker === 'stall' ? rvs : stalls}
          onClose={() => setShowPicker(null)}
          onConfirm={(picks) => {
            if (showPicker === 'stall') setStalls(picks);
            else setRvs(picks);
            setShowPicker(null);
          }}
        />
      )}
    </div>
  );
};

const PriceInput = ({ label, value, onChange, testid }) => (
  <div>
    <label className="block text-[10px] uppercase tracking-wider text-[#9E9E9E] mb-1">{label}</label>
    <div className="relative">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9E9E9E] text-sm">$</span>
      <input
        type="number" step="0.01" min="0" value={value}
        onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
        className="w-full pl-6 pr-3 py-2 bg-[#161616] border border-[#262626] rounded text-white text-sm focus:outline-none focus:border-[#FF9500]"
        data-testid={testid}
      />
    </div>
  </div>
);

const TotalRow = ({ label, value }) => (
  <div className="flex items-center justify-between text-xs py-0.5">
    <span className="text-[#9E9E9E]">{label}</span>
    <span className="text-white font-mono">${(value || 0).toFixed(2)}</span>
  </div>
);

const SpotSection = ({ label, icon: Icon, color, unit, spots, onAdd, onRemove, testid }) => (
  <div>
    <div className="flex items-center justify-between mb-2">
      <label className="text-[10px] uppercase tracking-wider text-[#9E9E9E] flex items-center gap-1">
        <Icon size={12} style={{ color }} /> {label}{' '}
        <span className="ml-1 text-white">({spots.length})</span>
      </label>
      <button
        onClick={onAdd}
        className="text-[#FF9500] hover:text-[#FFA500] text-xs font-bold inline-flex items-center gap-1"
        data-testid={`staff-invoice-pick-${testid}`}
      >
        <Plus size={12} weight="bold" /> Pick from map
      </button>
    </div>
    {spots.length === 0 ? (
      <p className="text-xs text-[#555] italic">No spots assigned.</p>
    ) : (
      <div className="flex flex-wrap gap-1.5">
        {spots.map((s, i) => (
          <span
            key={`${s.location_id}-${s.barn}-${s.row}-${s.spot_number}-${i}`}
            className="inline-flex items-center gap-1.5 px-2 py-1 bg-[#161616] border rounded text-xs"
            style={{ borderColor: color }}
            data-testid={`staff-invoice-${testid}-chip-${i}`}
          >
            <span className="text-white font-mono">{s.barn} {s.row}-{s.spot_number}</span>
            <span className="text-[#9E9E9E]">${Number(unit || 0).toFixed(0)}</span>
            <button onClick={() => onRemove(i)} className="text-[#FF3B30] hover:text-white">
              <X size={10} />
            </button>
          </span>
        ))}
      </div>
    )}
  </div>
);

// =====================================================
// SpotPickerModal — list of all stalls/RVs grouped by barn
// with checkboxes; "available" status from spot-map. Items
// already in the OTHER list (e.g. already picked as RV
// when picking stalls) are hidden — same spot can't be both.
// =====================================================
const SpotPickerModal = ({ type, spotMap, selected, alreadySelectedOther, onClose, onConfirm }) => {
  // Pre-build a key set for currently-selected items so checkboxes work.
  const selKeys = useMemo(() => new Set(selected.map(spotKey)), [selected]);
  const [picked, setPicked] = useState(() => new Map(selected.map((s) => [spotKey(s), s])));
  const [filterTaken, setFilterTaken] = useState(true); // hide taken by default

  // Build flat list of barns+rows for this type from spot_map + extra_locations.
  const sections = useMemo(() => {
    if (!spotMap) return [];
    const out = [];
    const mainMap = type === 'stall' ? (spotMap.stall_map || []) : (spotMap.rv_map || []);
    out.push({ location_id: 'main', name: 'Main', barns: mainMap });
    (spotMap.extra_locations || []).filter((l) => l.type === type).forEach((loc) => {
      out.push({ location_id: loc.id, name: loc.name, barns: loc.map });
    });
    return out;
  }, [spotMap, type]);

  const toggle = (s) => {
    const k = spotKey(s);
    setPicked((prev) => {
      const next = new Map(prev);
      if (next.has(k)) next.delete(k);
      else next.set(k, s);
      return next;
    });
  };

  const confirm = () => {
    onConfirm(Array.from(picked.values()));
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/85 backdrop-blur flex items-center justify-center p-3" onClick={onClose}>
      <div
        className="bg-[#0A0A0A] border border-[#1F1F1F] rounded-xl w-full max-w-2xl max-h-[88vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="staff-invoice-spot-picker"
      >
        <div className="px-5 py-4 border-b border-[#1F1F1F] flex items-center justify-between">
          <h3 className="text-base font-bold text-white" style={{ fontFamily: 'Outfit' }}>
            Pick {type === 'stall' ? 'Stalls' : 'RV Hookups'} — {picked.size} selected
          </h3>
          <button onClick={onClose} className="text-[#9E9E9E] hover:text-white"><X size={18} /></button>
        </div>

        <div className="px-5 py-3 border-b border-[#1F1F1F] flex items-center gap-3 text-xs">
          <label className="inline-flex items-center gap-2 text-[#9E9E9E] cursor-pointer">
            <input
              type="checkbox" checked={filterTaken} onChange={(e) => setFilterTaken(e.target.checked)}
              data-testid="staff-invoice-spot-picker-hide-taken"
            />
            Hide already-reserved spots
          </label>
          <span className="text-[#555]">·</span>
          <span className="text-[#9E9E9E]"><span className="inline-block w-2 h-2 rounded-full bg-[#34C759] mr-1" />Available</span>
          <span className="text-[#9E9E9E]"><span className="inline-block w-2 h-2 rounded-full bg-[#FF3B30] mr-1" />Taken</span>
        </div>

        <div className="overflow-y-auto px-5 py-4 space-y-5 flex-1">
          {sections.length === 0 ? (
            <p className="text-sm text-[#9E9E9E] py-6 text-center">No {type === 'stall' ? 'stall' : 'RV'} chart configured for this show.</p>
          ) : sections.map((sec) => (
            <div key={sec.location_id}>
              <div className="text-[10px] uppercase tracking-wider text-[#9E9E9E] mb-2">{sec.name}</div>
              {sec.barns.length === 0 ? (
                <p className="text-xs text-[#555] italic">No barns.</p>
              ) : sec.barns.map((barn, bi) => (
                <div key={bi} className="mb-3">
                  <div className="text-xs font-bold text-white mb-1">{barn.barn || 'Main'}</div>
                  {(barn.rows || []).map((row) => (
                    <div key={row.label} className="flex items-start gap-2 mb-1">
                      <span className="text-[10px] font-mono text-[#9E9E9E] w-12 pt-1">Row {row.label}</span>
                      <div className="flex flex-wrap gap-1 flex-1">
                        {(row.spots || []).map((sp) => {
                          const isTaken = sp.status !== 'available';
                          const otherKey = spotKey({ location_id: sec.location_id, barn: barn.barn, row: row.label, spot_number: sp.number });
                          const inOther = alreadySelectedOther.some((o) => spotKey(o) === otherKey);
                          if (filterTaken && isTaken && !selKeys.has(otherKey)) return null;
                          if (inOther) return null;
                          const picked_ = picked.has(otherKey);
                          return (
                            <button
                              key={sp.number}
                              onClick={() => toggle({ location_id: sec.location_id, barn: barn.barn, row: row.label, spot_number: sp.number, label: `${barn.barn} ${row.label}-${sp.number}` })}
                              disabled={isTaken && !selKeys.has(otherKey)}
                              className={`w-9 h-9 text-[10px] font-bold rounded border transition-all ${
                                picked_
                                  ? 'bg-[#FF9500] text-black border-[#FF9500]'
                                  : isTaken
                                    ? 'bg-[#1A0A0A] text-[#FF3B30] border-[#FF3B30]/30 cursor-not-allowed'
                                    : 'bg-[#0F1F0F] text-[#34C759] border-[#34C759]/30 hover:bg-[#34C759]/15'
                              }`}
                              data-testid={`spot-${type}-${sec.location_id}-${barn.barn}-${row.label}-${sp.number}`}
                            >
                              {sp.number}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          ))}
        </div>

        <div className="px-5 py-4 border-t border-[#1F1F1F] flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-[#9E9E9E] hover:text-white text-sm font-bold">Cancel</button>
          <button
            onClick={confirm}
            className="px-5 py-2 bg-[#FF9500] hover:bg-[#FFA500] text-black text-sm font-bold rounded"
            data-testid="staff-invoice-spot-picker-confirm"
          >
            Use {picked.size} spot{picked.size === 1 ? '' : 's'}
          </button>
        </div>
      </div>
    </div>
  );
};

function spotKey(s) {
  return `${s.location_id || 'main'}_${s.barn}_${s.row}_${s.spot_number}`;
}
